// M13.4 — pure-string stacked area SVG builder for the Ecommerce PDF page.
//
// Mirrors the React EcommerceChart shape but emits a flat
// `<svg>...</svg>` string with no DOM, no React, no event handlers —
// safe to call from inside a Cloudflare Worker where DocRaptor's
// static-HTML pipeline picks it up. Same top-N + Other rollup as the
// dashboard chart so the two surfaces tell the same visual story.
//
// Differs from source-mix-svg.ts: no `stackOffsetExpand` (we want
// absolute revenue bands, not 100%-normalised) and currency-formatted
// Y axis labels driven by the property's resolved currency code.

import { extent, max, sum } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack } from "d3-shape";

export interface EcommerceTrendRow {
  date: string;
  channel: string;
  revenue: number;
  purchases: number;
}

export interface BuildEcommerceSvgOpts {
  width?: number;
  height?: number;
  granularity: "day" | "week" | "month";
  /** Defaults to 8. Anything past this rolls into a single "Other" band. */
  topBandLimit?: number;
  /** ISO 4217 currency code for Y-axis tick labels. Null = plain
   *  numbers (graceful fallback for properties with no resolved code). */
  currencyCode: string | null | undefined;
}

const DEFAULT_WIDTH = 680;
const DEFAULT_HEIGHT = 240;
const MARGIN = { top: 24, right: 16, bottom: 28, left: 56 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";
const OTHER_COLOR = "#A1A1AA";
const OTHER_LABEL = "Other";

// Mirrors src/client/components/report/channel-colors.ts. Kept inline
// (not imported) so the shared/PDF builder stays free of any client
// dependencies — the PDF runs in the Worker queue consumer.
const CHANNEL_COLOR: Record<string, string> = {
  Direct: "#0E1680",
  "Organic Search": "#7C5CFF",
  "Paid Search": "#F59E0B",
  "Organic Social": "#4F31FF",
  "Paid Social": "#9583FF",
  Referral: "#06B6D4",
  Email: "#A855F7",
  Display: "#F97316",
  Affiliates: "#EC4899",
  Audio: "#14B8A6",
  SMS: "#10B981",
  Video: "#EF4444",
  "Mobile Push Notifications": "#84CC16",
  "Cross-network": "#6366F1",
  Unassigned: "#71717A",
};

function channelColor(channel: string): string {
  return CHANNEL_COLOR[channel] ?? OTHER_COLOR;
}

function formatYAxisCurrency(value: number, currencyCode: string | null | undefined): string {
  if (!Number.isFinite(value)) return "—";
  if (currencyCode && /^[A-Z]{3}$/.test(currencyCode)) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value);
    } catch {
      // Unknown ISO 4217 code — fall through.
    }
  }
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Build a static SVG stacked-area chart of per-channel revenue.
 * Returns the inline `<svg>...</svg>` string ready to drop into the
 * print-html template.
 */
export function buildEcommerceSvg(rows: EcommerceTrendRow[], opts: BuildEcommerceSvgOpts): string {
  const width = opts.width ?? DEFAULT_WIDTH;
  const height = opts.height ?? DEFAULT_HEIGHT;
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const topBandLimit = opts.topBandLimit ?? 8;

  if (rows.length === 0) {
    return emptyChart(width, height);
  }

  // ---- bucket the long tail into "Other" --------------------------------
  const totals = new Map<string, number>();
  const dateSet = new Set<string>();
  for (const r of rows) {
    totals.set(r.channel, (totals.get(r.channel) ?? 0) + r.revenue);
    dateSet.add(r.date);
  }
  const sortedChannels = Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([ch]) => ch);
  const topChannels = sortedChannels.slice(0, topBandLimit);
  const otherChannels = new Set(sortedChannels.slice(topBandLimit));
  const includeOther = otherChannels.size > 0;
  const dates = Array.from(dateSet).sort();
  const keys = includeOther ? [...topChannels, OTHER_LABEL] : topChannels;

  // ---- per-(date, channel-or-Other) revenue matrix ---------------------
  const cellsByDate: Array<Record<string, number>> = dates.map(() => {
    const cells: Record<string, number> = {};
    for (const k of keys) cells[k] = 0;
    return cells;
  });
  const indexByDate = new Map(dates.map((d, i) => [d, i]));
  for (const r of rows) {
    const idx = indexByDate.get(r.date);
    if (idx === undefined) continue;
    const key = otherChannels.has(r.channel) ? OTHER_LABEL : r.channel;
    cellsByDate[idx][key] = (cellsByDate[idx][key] ?? 0) + r.revenue;
  }

  // ---- d3-stack + scales -------------------------------------------------
  const stackGen = d3Stack<Record<string, number>>().keys(keys);
  const stacked = stackGen(cellsByDate);
  const dateObjs = dates.map((d) => new Date(`${d}T00:00:00Z`));
  const [xMin, xMax] = extent(dateObjs) as [Date, Date];
  const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
  const yMax = yMaxRaw === 0 ? 1 : yMaxRaw * 1.1;
  const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
  const yScale = scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);
  const xTickCount = opts.granularity === "day" ? 6 : opts.granularity === "week" ? 5 : 4;
  const xTicks = xScale.ticks(xTickCount);
  const yTickValues = yScale.ticks(4);

  const areaGen = d3Area<{ 0: number; 1: number }>()
    .x((_, i) => xScale(dateObjs[i]))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]));

  // ---- emit SVG ---------------------------------------------------------
  const yAxis = yTickValues
    .map((v) => {
      const y = yScale(v);
      return `
      <line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="${GRID_COLOR}" />
      <text x="-8" y="${y + 3}" font-size="10" fill="${AXIS_COLOR}" text-anchor="end">${escapeText(formatYAxisCurrency(v, opts.currencyCode))}</text>
    `;
    })
    .join("");

  const xAxis = xTicks
    .map((d) => {
      const x = xScale(d);
      return `
      <line x1="${x}" y1="${innerH}" x2="${x}" y2="${innerH + 4}" stroke="${AXIS_COLOR}" />
      <text x="${x}" y="${innerH + 16}" font-size="10" fill="${AXIS_COLOR}" text-anchor="middle">${escapeText(formatXTick(d, opts.granularity))}</text>
    `;
    })
    .join("");

  const bands = stacked
    .map((s) => {
      const path = areaGen(s as unknown as Array<{ 0: number; 1: number }>);
      if (!path) return "";
      const fill = s.key === OTHER_LABEL ? OTHER_COLOR : channelColor(String(s.key));
      return `<path d="${path}" fill="${fill}" stroke="#ffffff" stroke-width="0.5" opacity="0.92" />`;
    })
    .join("");

  // Total revenue across the window — used in the chart caption beneath
  // so the static export tells the reader the rolled-up number too.
  const totalRevenue = sum(stacked, (s) => sum(s, (d) => d[1] - d[0]));

  return `
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: ui-sans-serif, system-ui, sans-serif;">
  <g transform="translate(${MARGIN.left}, ${MARGIN.top})">
    ${yAxis}
    ${xAxis}
    ${bands}
  </g>
  <text x="${width - MARGIN.right}" y="${height - 4}" font-size="9" fill="${AXIS_COLOR}" text-anchor="end">Total: ${escapeText(formatYAxisCurrency(totalRevenue, opts.currencyCode))}</text>
</svg>
  `;
}

function emptyChart(width: number, height: number): string {
  return `
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
  <rect width="100%" height="100%" fill="#FAFAFA" />
  <text x="50%" y="50%" font-size="11" fill="#9CA3AF" text-anchor="middle" dominant-baseline="middle" font-family="ui-sans-serif, system-ui, sans-serif">
    No ecommerce activity in the selected window.
  </text>
</svg>
  `;
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function escapeText(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return ch;
    }
  });
}
